<template>
  <PageBreadcrumb
    :crumbs="[{
      label: 'Gestione Partecipanti'
    }]"
  />
  <div class="flex justify-between mb-4">
    <div class="flex-grow">
      <SearchInput v-model="search" />
      <SearchSelect
        v-model="disabledStatus"
        name="user-disabled-status"
        :options="{
          '1': 'Bloccati',
          '0': 'Attivi',
        }"
      />
      <AffiliateFilterSelect
        v-model="affiliateUuid"
        name="affiliate-filter"
      />
    </div>
    <div class="self-end">
      <ActionButton
        v-if="authStore.canManageUser"
        @click="router.push('/partecipanti/crea')"
      >
        <PlusCircleIcon class="w-5 h-5 inline-block" />
        Crea nuova partecipante
      </ActionButton>
    </div>
  </div>

  <DataTable :data="data">
    <template #heading>
      <tr>
        <SortableColumn
          v-model="sortStatus"
          name="name"
        >
          Nome
        </SortableColumn>
        <th class="text-right p-2" />
      </tr>
    </template>
    <template #body>
      <tr
        v-for="company in data"
        :key="'company-table-' + company.uuid"
        class="odd:bg-gray-100"
        :class="{ 'text-gray-300': company.disabled }"
      >
        <td class="p-2">
          {{ company.name }}
        </td>
        <td
          class="p-2 text-right"
          :class="{ 'text-corepla-text': company.disabled }"
        >
          <RouterLink
            v-if="authStore.canManageUser"
            title="Modifica Partecipante"
            :to="'/partecipanti/' + company.uuid"
          >
            <PencilSquareIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <RouterLink
            v-if="authStore.isAdmin"
            title="Guarda utenti"
            :to="'/utenti?companyUuid=' + company.uuid"
          >
            <UserIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <RouterLink
            v-if="authStore.canManageUser"
            title="Gestisci Categorie"
            :to="'/partecipanti/' + company.uuid + '/categorie'"
          >
            <Square3Stack3DIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <RouterLink
            v-if="authStore.canManageUser"
            title="Gestisci Codici Rifiuto"
            :to="'/partecipanti/' + company.uuid + '/codici-rifiuto'"
          >
            <ArrowPathRoundedSquareIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <RouterLink
            v-if="authStore.canManageUser"
            title="Gestisci Gruppi di Partecipanti"
            :to="'/partecipanti/' + company.uuid + '/gruppi-partecipanti'"
          >
            <RectangleGroupIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <a
            v-if="!company.disabled && authStore.canManageUser"
            href="#"
            title="Blocca Partecipante"
            @click.prevent="block(company)"
          >
            <PauseCircleIcon class="h-5 w-5 inline-block mx-1 " />
          </a>
          <a
            v-if="company.disabled && authStore.canManageUser"
            href="#"
            title="Ripristina Partecipante"
            @click.prevent="unblock(company)"
          >
            <PlayCircleIcon class="h-5 w-5 inline-block mx-1 " />
          </a>
          <a
            href="#"
            title="Elimina Partecipante"
            @click.prevent="destroy(company)"
          >
            <TrashIcon class="h-5 w-5 inline-block mx-1 " />
          </a>
        </td>
      </tr>
    </template>
  </DataTable>

  <DataTablePaginator
    :count="count"
    :count-all="countAll"
    :count-filtered="countFiltered"
    :current-page="currentPage"
    :has-next="hasNext"
    :has-prev="hasPrev"
    :next="next"
    :prev="prev"
    :total-pages="totalPages"
  />
</template>
    
<script setup>
import {
  ArrowPathRoundedSquareIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  PlusCircleIcon,
  RectangleGroupIcon,
  Square3Stack3DIcon,
  TrashIcon,
  UserIcon
} from "@heroicons/vue/24/outline";
import { ref, watch } from "vue";
import { useRoute,useRouter } from 'vue-router'

import ActionButton from "@/components/ActionButton.vue";
import DataTable from "@/components/list/DataTable.vue";
import DataTablePaginator from "@/components/list/DataTablePaginator.vue";
import AffiliateFilterSelect from "@/components/list/filters/AffiliateFilterSelect.vue";
import SearchInput from "@/components/list/SearchInput.vue";
import SearchSelect from "@/components/list/SearchSelect.vue";
import SortableColumn from "@/components/list/SortableColumn.vue";
import PageBreadcrumb from "@/components/PageBreadcrumb.vue";
import useDataTable from '@/composables/datatable.js'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const search = ref('')
const disabledStatus = ref(null);
const affiliateUuid = ref(null);

const sortStatus = ref(["name", -1]);

const { data, count, countFiltered, countAll, currentPage, totalPages, next, prev, hasPrev, hasNext, doFilter, action } = await useDataTable({
  endpoint: "/company/list",
  sortStatus,
  search,
  searchProperties: ['name'],
  extraFilter: (item) => {
    return (disabledStatus.value ? item.disabled == disabledStatus.value : true) &&
      (affiliateUuid.value ? item.affiliateUuid === affiliateUuid.value : true);
  }
})

watch(disabledStatus, () => {
  doFilter()
})

watch(affiliateUuid, () => {
  doFilter()
})

affiliateUuid.value = route.query.affiliateUuid;

const destroy = (auction) => {
  action('delete', 'company/' + auction.uuid, 'Partecipante eliminata con successo')
}

const block = (user) => {
  action('put', 'company/block/' + user.uuid, 'Partecipante bloccata con successo')
}

const unblock = (user) => {
  action('put', 'company/unblock/' + user.uuid, 'Partecipante ripristinata con successo')
}
</script>
